// Three.js converts Euler degrees to radians on replay and back to degrees on
// serialization. Raw IEEE-754 results can differ by one final bit each trip,
// which makes otherwise identical replicas disagree byte-for-byte. Twelve
// decimal places retains substantially more precision than scene interaction
// needs while establishing a deterministic fixed point for persisted numbers.
export function canonicalNumber(value) {
  if (!Number.isFinite(value)) throw new Error(`cannot serialize non-finite number ${String(value)}`);
  const rounded = Number(value.toFixed(12));
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function canonicalAngleDegrees(value) {
  if (!Number.isFinite(value)) return canonicalNumber(value);
  // Euler components are independently 360-periodic for the fixed XYZ order.
  // Reduce first so degree/radian conversion cannot cross a rounding boundary
  // at a large equivalent angle. The half-open range gives 180 one spelling.
  let bounded = ((value % 360) + 360) % 360;
  if (bounded >= 180) bounded -= 360;
  const rounded = canonicalNumber(bounded);
  // Rounding a value infinitesimally below the upper boundary can recreate
  // +180. Keep the range half-open after rounding as well as before it.
  return rounded >= 180 ? -180 : rounded;
}

export function canonicalRotation(values) {
  return values.map(canonicalAngleDegrees);
}

export function canonicalValue(value) {
  if (typeof value === 'number') return canonicalNumber(value);
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, canonicalValue(child)]));
  }
  return value;
}

function canonicalJsonValue(value) {
  if (typeof value === 'number') return canonicalNumber(value);
  if (Array.isArray(value)) return value.map(canonicalJsonValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, canonicalJsonValue(value[key])]),
    );
  }
  return value;
}

export function canonicalJson(value) {
  return JSON.stringify(canonicalJsonValue(value));
}
